<template>
  <!-- Anonymous state -->
  <div
    v-if="selectedData.isAnonymous"
    class="of-anonymous"
  >
    <i class="fa-light fa-user-secret" />
    <span>{{ t('dataExplorer.anonymous') }}</span>
  </div>

  <!-- Order file -->
  <div
    v-else
    class="of"
  >
    <!-- ===== HERO HEADER ===== -->
    <div class="of-hero">
      <div class="of-hero__left">
        <div class="of-hero__avatar">
          <i class="fa-light fa-bag-shopping" />
        </div>
        <div class="of-hero__identity">
          <h2 class="of-hero__name">
            {{ title }}
          </h2>
          <div class="of-hero__meta">
            <span class="of-hero__id">#{{ selectedData.idOrder }}</span>
            <span
              v-if="selectedData.statusLoaded && selectedData.status"
              class="of-hero__badge"
            >
              <i class="fas fa-circle-dot" />
              {{ selectedData.status }}
            </span>
            <span
              v-if="sourceLabel"
              class="of-hero__source"
            >
              <i class="far fa-database" />
              {{ sourceLabel }}
            </span>
            <Skeleton
              v-else-if="!selectedData.statusLoaded"
              width="5rem"
              height="1.2rem"
              border-radius="20px"
            />
          </div>
        </div>
      </div>
      <div class="of-hero__dates">
        <div class="of-hero__date">
          <i class="fa-light fa-calendar-plus" />
          <span class="of-hero__date-label">{{ t('dataExplorer.orderFile.fields.creationDate') }}</span>
          <span class="of-hero__date-value">{{ formatDate(selectedData.dateCreation, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY') }}</span>
        </div>
        <span class="of-hero__date-sep" />
        <div class="of-hero__date">
          <i class="fa-light fa-calendar-edit" />
          <span class="of-hero__date-label">{{ t('dataExplorer.orderFile.fields.modificationDate') }}</span>
          <span class="of-hero__date-value">{{ formatDate(selectedData.dateModification, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY') }}</span>
        </div>
      </div>
    </div>

    <!-- ===== MAIN 2-COL LAYOUT ===== -->
    <div class="of-layout">
      <!-- ===== LEFT COLUMN: Order info ===== -->
      <div class="of-left">
        <!-- Customer info -->
        <div class="of-card">
          <div class="of-card__header">
            <i class="fa-light fa-user" />
            <h3>{{ t('dataExplorer.orderFile.fields.customerInformations') }}</h3>
          </div>
          <div class="of-card__body">
            <div class="of-info-row">
              <div class="of-info-row__icon">
                <i class="fa-light fa-id-badge" />
              </div>
              <div class="of-info-row__content">
                <span class="of-info-row__label">{{ t('dataExplorer.orderFile.fields.customer') }}</span>
                <span class="of-info-row__value">
                  <span class="capitalize">{{ selectedData.firstname }}</span> {{ (selectedData.lastname || '').toUpperCase() }}
                  <span
                    v-if="selectedData.idCustomerShop"
                    class="of-info-row__sub"
                  >({{ selectedData.idCustomerShop }})</span>
                </span>
              </div>
            </div>
            <div class="of-info-row">
              <div class="of-info-row__icon">
                <i class="fa-light fa-envelope" />
              </div>
              <div class="of-info-row__content">
                <span class="of-info-row__label">{{ t('dataExplorer.orderFile.fields.email') }}</span>
                <span class="of-info-row__value">
                  <DataExplorerLink
                    :id="selectedData.idShopContact"
                    :value="selectedData.email"
                    :show-icon="true"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Order details -->
        <div class="of-card">
          <div class="of-card__header">
            <i class="fa-light fa-clipboard-list" />
            <h3>{{ t('dataExplorer.orderFile.fields.orderInformations') }}</h3>
          </div>
          <div class="of-card__body">
            <div class="of-info-row">
              <div class="of-info-row__icon">
                <i class="fa-light fa-calendar" />
              </div>
              <div class="of-info-row__content">
                <span class="of-info-row__label">{{ t('dataExplorer.orderFile.fields.orderDate') }}</span>
                <span class="of-info-row__value">{{ formatDate(selectedData.date, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY HH:mm:ss') }}</span>
              </div>
            </div>
            <div class="of-info-row">
              <div class="of-info-row__icon">
                <i class="fa-light fa-shopping-basket" />
              </div>
              <div class="of-info-row__content">
                <span class="of-info-row__label">{{ t('dataExplorer.orderFile.fields.idCart') }}</span>
                <span class="of-info-row__value">
                  <DataExplorerLink
                    :id="selectedData.idCart"
                    :value="selectedData.idCart"
                    :type="dataExplorerType.CART_FILE"
                    :show-icon="true"
                  />
                </span>
              </div>
            </div>
            <div class="of-info-row">
              <div class="of-info-row__icon">
                <i class="fa-light fa-clock" />
              </div>
              <div class="of-info-row__content">
                <span class="of-info-row__label">{{ t('dataExplorer.orderFile.fields.dateCart') }}</span>
                <span class="of-info-row__value">{{ formatDate(selectedData.dateCart, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY HH:mm:ss') }}</span>
              </div>
            </div>
            <div class="of-info-row">
              <div class="of-info-row__icon">
                <i class="fa-light fa-circle-check" />
              </div>
              <div class="of-info-row__content">
                <span class="of-info-row__label">{{ t('dataExplorer.orderFile.fields.isValid') }}</span>
                <span class="of-info-row__value">
                  <span
                    class="of-validity-badge"
                    :class="selectedData.isValid === '1' ? 'of-validity-badge--yes' : 'of-validity-badge--no'"
                  >
                    {{ selectedData.isValid === '1' ? t('dataExplorer.contactFile.fields.account.hasAccount.value.yes') : t('dataExplorer.contactFile.fields.account.hasAccount.value.no') }}
                  </span>
                </span>
              </div>
            </div>
            <div
              v-if="selectedData.carrierName"
              class="of-info-row"
            >
              <div class="of-info-row__icon">
                <i class="fa-light fa-truck" />
              </div>
              <div class="of-info-row__content">
                <span class="of-info-row__label">{{ t('dataExplorer.orderFile.fields.carrier') }}</span>
                <span class="of-info-row__value">
                  {{ selectedData.carrierName }}
                  <span
                    v-if="selectedData.shippingNumber"
                    class="of-info-row__sub"
                  >({{ selectedData.shippingNumber }})</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Delivery address -->
        <div class="of-card">
          <div class="of-card__header">
            <i class="fa-light fa-location-dot" />
            <h3>{{ t('dataExplorer.orderFile.fields.deliveryInformations') }}</h3>
          </div>
          <div class="of-card__body">
            <Skeleton
              v-if="loadingDeliveryAddress"
              height="3rem"
              class="of-card__skeleton"
            />
            <div
              v-else-if="selectedData.deliveryAddress.data.length"
              class="of-card__address"
            >
              <AddressItem
                :address="selectedData.deliveryAddress.data[0]"
                :show-to-details-button="false"
                :show-map-button="true"
              />
            </div>
            <div
              v-else
              class="of-card__empty"
            >
              -
            </div>
          </div>
        </div>

        <!-- Invoice address -->
        <div class="of-card">
          <div class="of-card__header">
            <i class="fa-light fa-file-invoice" />
            <h3>{{ t('dataExplorer.orderFile.fields.invoiceInformations') }}</h3>
          </div>
          <div class="of-card__body">
            <Skeleton
              v-if="loadingInvoiceAddress"
              height="3rem"
              class="of-card__skeleton"
            />
            <div
              v-else-if="selectedData.invoiceAddress.data.length"
              class="of-card__address"
            >
              <AddressItem
                :address="selectedData.invoiceAddress.data[0]"
                :show-to-details-button="false"
                :show-map-button="true"
              />
            </div>
            <div
              v-else
              class="of-card__empty"
            >
              -
            </div>
          </div>
        </div>
      </div>

      <!-- ===== RIGHT COLUMN: Products & summary ===== -->
      <div class="of-right">
        <div class="of-card of-card--full">
          <div class="of-card__header of-card__header--no-border">
            <i class="fa-light fa-boxes-stacked" />
            <h3>{{ t('dataExplorer.orderFile.fields.orderDetails') }}</h3>
          </div>
          <div class="of-card__body of-card__body--padded">
            <!-- Commande dont aucun produit n'est synchronisé avec ShopiMind :
                 message clair pour le marchand. La commande reste comptée (en-tête,
                 résumé, stats, récence) ; seul le détail des articles est indisponible.
                 La <List> ci-dessous porte le chargement (v-else) : tant que les
                 produits ne sont pas chargés, elle s'affiche et déclenche le fetch ;
                 une fois chargée et vide, on bascule sur ce message. -->
            <div
              v-if="selectedData.products.dataLoaded && !selectedData.products.data.length"
              class="of-no-products"
            >
              <div class="of-no-products__icon">
                <i class="fa-light fa-box-open" />
              </div>
              <div class="of-no-products__body">
                <h4 class="of-no-products__title">
                  {{ t('dataExplorer.orderFile.noProducts.title') }}
                </h4>
                <p class="of-no-products__text">
                  {{ t('dataExplorer.orderFile.noProducts.description') }}
                </p>
                <span class="of-no-products__reasons-title">
                  {{ t('dataExplorer.orderFile.noProducts.reasonsTitle') }}
                </span>
                <ul class="of-no-products__reasons">
                  <li>{{ t('dataExplorer.orderFile.noProducts.reason1') }}</li>
                  <li>{{ t('dataExplorer.orderFile.noProducts.reason2') }}</li>
                  <li>{{ t('dataExplorer.orderFile.noProducts.reason3') }}</li>
                </ul>
              </div>
            </div>
            <List
              v-else
              :list="selectedData.products.data"
              :retrieve-data="getOrdersProducts"
              :retrieve-data-arguments="[selectedData.idShopOrder, locale]"
              :data-loaded="selectedData.products.dataLoaded"
              :no-column="true"
              key-name="idShopOrderProduct"
              @loaded="handleRetrieveOrderProducts"
            >
              <template #content="{ item }">
                <ProductItem
                  :product="item"
                />
              </template>
            </List>

            <!-- Price summary -->
            <div class="of-summary">
              <div class="of-summary__title">
                {{ t('dataExplorer.orderFile.fields.summary') }}
              </div>
              <div
                v-if="selectedData.shippingHT !== null && selectedData.shippingHT !== undefined"
                class="of-summary__row"
              >
                <span class="of-summary__label">{{ t('dataExplorer.orderFile.fields.shippingHT') }}</span>
                <span class="of-summary__value">{{ formatNumberToCurrency(selectedData.shippingHT, selectedData.currency) }}</span>
              </div>
              <div
                v-if="selectedData.shipping !== null && selectedData.shipping !== undefined"
                class="of-summary__row"
              >
                <span class="of-summary__label">{{ t('dataExplorer.orderFile.fields.shippingTTC') }}</span>
                <span class="of-summary__value">{{ formatNumberToCurrency(selectedData.shipping, selectedData.currency) }}</span>
              </div>
              <div
                v-if="selectedData.voucherAmount"
                class="of-summary__row"
              >
                <span class="of-summary__label">{{ t('dataExplorer.orderFile.fields.voucherAmount') }}</span>
                <span class="of-summary__value">{{ formatNumberToCurrency(selectedData.voucherAmount, selectedData.currency) }}</span>
              </div>
              <div class="of-summary__row of-summary__row--border">
                <span class="of-summary__label of-summary__label--bold">{{ t('dataExplorer.orderFile.fields.amountHT') }}</span>
                <span class="of-summary__value of-summary__value--bold">{{ formatNumberToCurrency(selectedData.amountHT, selectedData.currency) }}</span>
              </div>
              <div class="of-summary__row">
                <span class="of-summary__label of-summary__label--bold">{{ t('dataExplorer.orderFile.fields.amountTTC') }}</span>
                <span class="of-summary__total">{{ formatNumberToCurrency(selectedData.amount, selectedData.currency) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script lang="ts">
import {
  defineComponent,
  computed,
  onMounted,
  ref,
} from 'vue';

import Skeleton from 'primevue/skeleton';

import AddressItem from '@/components/data-explorer/fields/AddressItem.vue';
import DataExplorerLink from '@/components/data-explorer/fields/DataExplorerLink.vue';
import ProductItem from '@/components/data-explorer/fields/ProductItem.vue';
import List from "@/components/data-explorer/fields/List.vue";

import {
  getAddressByIdShopAddress,
  getOrdersProducts,
  getOrderStatus,
} from '@/composables/data-explorer/DataExplorer';

import { fetchDataSources } from '@/composables/segmentations/helpers/fetchData';

import { formatDate } from '@/helpers/Date';
import { formatNumberToCurrency } from '@/helpers/Number';

import { DataExplorerDataType } from '@/types';
import { OrderProduct } from '@/types/data-explorer-types';

import { useStore } from '@/store';

import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'OrderFile',

  components: {
    AddressItem,
    DataExplorerLink,
    List,
    Skeleton,
    ProductItem,
  },

  setup() {
    const { t, locale } = useI18n();
    const store = useStore();

    const selectedData = computed(() => store.getters['dataExplorer/getSelectedData']);

    // Source label
    const sourceLabel = ref('');
    const loadSourceLabel = async () => {
      if (!selectedData.value?.dataSourceId) return;
      const sources = await fetchDataSources();
      const found = sources.find((s: any) => s.value === Number(selectedData.value.dataSourceId));
      if (found) sourceLabel.value = found.shortLabel || found.label;
    };

    const title = computed(() => {
      if (selectedData.value) {
        return t(`dataExplorer.tabs.${selectedData.value.dataType}`, [selectedData.value.orderReference])
      }
      return '';
    });

    const loadingDeliveryAddress = ref(false);
    const loadingInvoiceAddress = ref(false);

    const handleRetrieveOrderProducts = (orderProducts: OrderProduct[]) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          newLists: orderProducts,
          dataKey: 'products',
        }
      );
    };

    const dataExplorerType = ref(DataExplorerDataType);

    onMounted(async () => {
      loadSourceLabel();
      try {
        if (selectedData.value && !selectedData.value.statusLoaded) {
          const status = await getOrderStatus(selectedData.value.status, locale.value);
          if (status) {
            store.dispatch('dataExplorer/updateOrderFileStatus', { status });
          } else {
            store.dispatch('dataExplorer/updateOrderFileStatus', { status: '' });
          }
        }

        loadingDeliveryAddress.value = true;
        loadingInvoiceAddress.value = true;

        const dispatchAddress = (newLists: any[], dataKey: string) => {
          store.dispatch(
              'dataExplorer/setAdditionnalData',
              {
                dataId: selectedData.value.dataId,
                newLists,
                dataKey,
              }
            );
        }

        if (selectedData.value && !selectedData.value.deliveryAddress.dataLoaded) {
          const deliveryAddress = await getAddressByIdShopAddress(selectedData.value.idAddressDelivery);
          if (deliveryAddress) {
            dispatchAddress([deliveryAddress], 'deliveryAddress');
          } else {
            dispatchAddress([], 'deliveryAddress');
          }
        }
        if (selectedData.value && !selectedData.value.invoiceAddress.dataLoaded) {
          const invoiceAddress = await getAddressByIdShopAddress(selectedData.value.idAddressDelivery);
          if (invoiceAddress) {
            dispatchAddress([invoiceAddress], 'invoiceAddress');
          } else {
            dispatchAddress([], 'invoiceAddress');
          }
        }
      } catch (err) {
        // Empty
      } finally {
        loadingDeliveryAddress.value = false;
        loadingInvoiceAddress.value = false;
      }
    });

    return {
      t,
      locale,
      title,
      selectedData,
      sourceLabel,
      loadingDeliveryAddress,
      loadingInvoiceAddress,
      dataExplorerType,

      formatDate,
      formatNumberToCurrency,
      getOrdersProducts,
      handleRetrieveOrderProducts,
    }
  }
})
</script>

<style lang="scss" scoped>
// ===== ANONYMOUS =====
.of-anonymous {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 2rem;
  color: #9ca3af;
  font-size: 1.25rem;

  i {
    font-size: 3rem;
    opacity: 0.5;
  }
}

// ===== MAIN CONTAINER =====
.of {
  padding: 1.25rem;
}

// ===== HERO HEADER =====
.of-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #e5e7eb;

  &__left {
    display: flex;
    align-items: center;
    gap: 0.875rem;
  }

  &__avatar {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #f59e0b, #d97706);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      color: #fff;
      font-size: 1.25rem;
    }
  }

  &__identity {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__name {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    line-height: 1.2;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__id {
    font-size: 0.75rem;
    color: #64748b;
    font-weight: 600;
    background: #f1f5f9;
    padding: 0.0625rem 0.4375rem;
    border-radius: 4px;
    font-family: monospace;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 0.125rem 0.5rem;
    border-radius: 20px;
    background: #dbeafe;
    color: #1d4ed8;

    i { font-size: 0.5rem; }
  }

  &__source {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 0.125rem 0.5rem;
    border-radius: 20px;
    background: #e0e7ff;
    color: #3730a3;

    i { font-size: 0.5rem; }
  }

  &__dates {
    display: flex;
    align-items: center;
    gap: 0.625rem;
  }

  &__date {
    display: flex;
    align-items: center;
    gap: 0.3125rem;
    font-size: 0.6875rem;
    color: #94a3b8;

    i { font-size: 0.8125rem; }
  }

  &__date-label {
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__date-value {
    font-weight: 700;
    color: #475569;
  }

  &__date-sep {
    width: 1px;
    height: 20px;
    background: #d1d5db;
  }
}

// ===== 2-COL LAYOUT =====
.of-layout {
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 1.25rem;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
}

.of-left {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.of-right {
  min-width: 0;
}

// ===== CARD =====
.of-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;

  &--full {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 0.4375rem;
    padding: 0.625rem 1rem;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;

    &--no-border {
      border-bottom: none;
    }

    i {
      color: #f59e0b;
      font-size: 0.875rem;
    }

    h3 {
      font-size: 0.75rem;
      font-weight: 700;
      color: #374151;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin: 0;
    }
  }

  &__body {
    padding: 0.5rem 0;

    &--padded {
      padding: 0.75rem 1rem;
    }
  }

  &__skeleton {
    margin: 0.5rem 1rem;
  }

  &__address {
    padding: 0.5rem 1rem;
  }

  &__empty {
    padding: 0.75rem 1rem;
    color: #d1d5db;
    font-size: 0.8125rem;
  }
}

// ===== INFO ROWS =====
.of-info-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  transition: background 0.1s;

  &:hover {
    background: #f9fafb;
  }

  &__icon {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 0.8125rem;
      color: #64748b;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.0625rem;
  }

  &__label {
    font-size: 0.6875rem;
    font-weight: 500;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__value {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__sub {
    color: #94a3b8;
    font-size: 0.75rem;
    font-weight: 400;
  }
}

// ===== VALIDITY BADGE =====
.of-validity-badge {
  display: inline-flex;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.0625rem 0.4375rem;
  border-radius: 4px;

  &--yes {
    background: #dcfce7;
    color: #166534;
  }

  &--no {
    background: #fee2e2;
    color: #991b1b;
  }
}

// ===== NO PRODUCTS (commande sans produit synchronisé) =====
.of-no-products {
  display: flex;
  gap: 0.875rem;
  padding: 1rem 1.125rem;
  border: 1px solid #fde68a;
  background: #fffbeb;
  border-radius: 10px;

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 9px;
    background: #fef3c7;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      color: #d97706;
      font-size: 1rem;
    }
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 0.875rem;
    font-weight: 700;
    color: #92400e;
    margin: 0 0 0.375rem;
  }

  &__text {
    font-size: 0.8125rem;
    line-height: 1.5;
    color: #78716c;
    margin: 0 0 0.625rem;
  }

  &__reasons-title {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #57534e;
    margin-bottom: 0.25rem;
  }

  &__reasons {
    margin: 0;
    padding-left: 1.125rem;

    li {
      font-size: 0.8125rem;
      line-height: 1.45;
      color: #78716c;
      margin-bottom: 0.25rem;
    }
  }
}

// ===== PRICE SUMMARY =====
.of-summary {
  margin-top: 1.25rem;
  border-top: 1px solid #e5e7eb;
  padding-top: 1rem;

  &__title {
    font-size: 0.8125rem;
    font-weight: 700;
    color: #374151;
    margin-bottom: 0.75rem;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.375rem 0;

    &--border {
      border-top: 1px solid #e5e7eb;
      margin-top: 0.375rem;
      padding-top: 0.75rem;
    }
  }

  &__label {
    font-size: 0.8125rem;
    color: #64748b;

    &--bold {
      font-weight: 600;
      color: #1e293b;
    }
  }

  &__value {
    font-size: 0.875rem;
    font-weight: 500;
    color: #1e293b;

    &--bold {
      font-weight: 700;
    }
  }

  &__total {
    font-size: 1.25rem;
    font-weight: 800;
    color: #f59e0b;
  }
}
</style>
